import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** The band boundaries. Kate's, via the ticket — the prototype's lower edge was 40. */
export const SERP_MATCH_BANDS = { highMin: 65, mediumMin: 35 } as const;

/** Fewer scored results than this and the number is greyed rather than hidden. */
export const SERP_MATCH_LOW_SAMPLE = 10;

/**
 * The low-sample treatment: the number is shown, but **greyed out — no band tint**, per the
 * ticket's Display bands section ("show the value greyed out with a 'low sample' tooltip").
 *
 * Keeping the band tint and merely dimming it was the first implementation here and it was wrong:
 * a dimmed green still asserts High, which is the one thing a three-of-forty-six answer must not
 * do. Grey says "we have a number and you should not lean on it".
 */
export const SERP_MATCH_LOW_SAMPLE_CLASS = "bg-muted text-muted-foreground";

/**
 * Tinted with the soft status tokens `RankingCell` uses, **not** the heat ramp the prototype tints
 * SERP Match with, so the two tinted columns on this one table agree with each other.
 */
export const SERP_MATCH_BAND_CLASSES = {
  high: "bg-status-success-soft",
  medium: "bg-status-warning-soft",
  low: "bg-status-error-soft",
} as const;

/**
 * A theme or Unthemed roll-up mean, tinted by the same bands as a scored cell.
 *
 * FR-7 asks for the mean "tinted by the same bands", the prototype renders it as a tinted pill, and
 * the Hero Rank roll-up sitting beside it on the same row is tinted too. It shipped as a plain
 * number, which made a theme's mean the one value on that row saying nothing about its band.
 *
 * Deliberately no low-sample treatment: whether a mean drawn mostly from low-sample keywords should
 * suppress itself is an open question on the spec, not something to settle here.
 */
export function SerpMatchRollupValue({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-xs text-muted-foreground">—</span>;
  const band = serpMatchBand(pct);
  return (
    <div className="inline-flex items-center justify-center gap-1">
      <span className="text-[10px] font-medium text-muted-foreground uppercase">Avg</span>
      <span
        data-testid="serp-match-rollup"
        data-band={band}
        className={cn(
          "inline-flex items-center justify-center rounded-md px-2 py-1 text-xs tabular-nums min-w-[2.75rem]",
          SERP_MATCH_BAND_CLASSES[band],
          "text-foreground",
        )}
      >
        {pct}%
      </span>
    </div>
  );
}

export type SerpMatch = {
  serp_match_pct: number | null;
  serp_match_organic_pct: number | null;
  serp_match_sponsored_pct: number | null;
  serp_match_scored: number | null;
  serp_match_found: number | null;
  serp_match_week: string | null;
};

export function serpMatchBand(pct: number): keyof typeof SERP_MATCH_BAND_CLASSES {
  if (pct >= SERP_MATCH_BANDS.highMin) return "high";
  if (pct >= SERP_MATCH_BANDS.mediumMin) return "medium";
  return "low";
}

const BAND_LABEL = { high: "High", medium: "Medium", low: "Low" } as const;

function formatWeek(week: string | null): string {
  if (!week) return "";
  const d = new Date(week);
  return Number.isNaN(d.getTime())
    ? week
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/**
 * AUT-496 Part 2. The share of a keyword's page-1 Amazon results whose listing resembles the hero
 * product (`specs/keyword/serp-match`).
 *
 * Three states, and telling them apart is the point:
 *
 * - **no data** — never scored, or nothing on page 1 could be read. A dash, never a 0: a 0 would
 *   read as "nothing resembles this product" when the truth is "we could not look".
 * - **low sample** — fewer than ten results scored. The number is shown greyed out, because
 *   discarding it loses real information but a band tint would assert a confidence it has not
 *   earned: a proportion of three reads exactly like a proportion of forty-six, and one tested
 *   keyword had text for only 3 of 46.
 * - **scored** — the number, tinted by band.
 *
 * The low-sample treatment is the ticket's own instruction, not a default — see
 * `SERP_MATCH_LOW_SAMPLE_CLASS`.
 */
export function SerpMatchCell({ keyword }: { keyword: SerpMatch }) {
  const pct = keyword.serp_match_pct;
  const scored = keyword.serp_match_scored ?? 0;
  const found = keyword.serp_match_found ?? 0;

  if (pct === null) {
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span className="text-muted-foreground" data-testid="serp-match-empty">
            —
          </span>
        </TooltipTrigger>
        <TooltipContent>No search results data for this keyword yet.</TooltipContent>
      </Tooltip>
    );
  }

  const band = serpMatchBand(pct);
  const lowSample = scored < SERP_MATCH_LOW_SAMPLE;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span
          data-testid="serp-match-value"
          data-band={band}
          data-low-sample={lowSample}
          className={cn(
            "inline-flex items-center justify-center rounded-md px-2 py-1 text-xs tabular-nums min-w-[2.75rem]",
            lowSample ? SERP_MATCH_LOW_SAMPLE_CLASS : cn(SERP_MATCH_BAND_CLASSES[band], "text-foreground"),
          )}
        >
          {Math.round(pct)}%
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">
        {lowSample ? (
          <>
            <p>
              <span className="font-semibold">Low sample.</span> Only {scored} of {found} page-1{" "}
              {found === 1 ? "product" : "products"} could be scored, so this number may not be
              reliable.
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold">
              {Math.round(pct)}% · {BAND_LABEL[band]}
            </p>
            <p>
              Organic: {keyword.serp_match_organic_pct === null ? "—" : `${Math.round(keyword.serp_match_organic_pct)}%`}
              {" · "}
              Sponsored: {keyword.serp_match_sponsored_pct === null ? "—" : `${Math.round(keyword.serp_match_sponsored_pct)}%`}
            </p>
            <p>
              Based on {found} page-1 {found === 1 ? "product" : "products"}
              {keyword.serp_match_week ? ` · Week of ${formatWeek(keyword.serp_match_week)}` : ""}
            </p>
          </>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
